import {
  idSchema,
  nameSchema,
  objectIdSchema,
  optionalStringSchema,
  paginationWithSearchSchema,
  saudiVehiclePlateSchema,
  Vehicle,
  VehicleSchema,
  vehicleOwnerKindSchema,
  vehicleOwnerTypeSchema,
  vehicleTypesSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

const classification = VehicleSchema.data.shape.classification.shape;
const registration = VehicleSchema.data.shape.registration.shape;

const schema = VehicleSchema.partial
  .pick({
    title: true,
    status: true,
  })
  .extend({
    /** the year of the car such as 2020, 2019, etc.
     *
     * Optional — recommended for Logisti integration.
     */
    year: classification.year,

    /** truck brand such as volvo, etc.
     *
     * Optional — recommended for Logisti integration.
     */
    brand: classification.brand,

    /** truck plate number. Hard requirement on vehicle creation. */
    plate: registration.plate,

    /**
     * Saudi traffic-authority plate category (private transport, public
     * transport, heavy equipment, …).
     *
     * Optional. Bayan requires `plateTypeId`, but when this is unset issuance
     * falls back to `BAYAN_CONFIG.defaults.plateType` (`public_transport`).
     * Set it explicitly for anything that isn't a for-hire freight truck.
     */
    plateType: registration.plateType,

    /** the color of the vehicle. Optional — recommended for Logisti. */
    color: classification.color,

    /** استمارة expiry. Optional — captured from photos when visible. */
    expiryDate: registration.expiryDate,
    /** الرقم التسلسلي on the استمارة. Optional. */
    serialNum: registration.serialNum,
    /** رقم الهيكل (chassis / VIN). Optional. */
    structureNumber: registration.structureNumber,

    /** a registration image. Optional — recommended for Logisti. */
    registrationImage: registration.image,
    vehicleImages: VehicleSchema.data.shape.images.optional(),

    /** the owner id ... because sometimes a carrier can add a vehicle to his system that he does not directly own.
     *
     * if empty, we will assume that the carrier is the owner.
     *
     * if very freelancer driver ... then owner will be the same as driver.
     */
    ownerId: objectIdSchema.optional(),

    /** the vehicle type to such as dyna_chilled, truck_flatbed, etc.
     *
     * Hard requirement on vehicle creation (alongside `plate`).
     */
    vehicle: vehicleTypesSchema,
    /** the origin of the country vehicle. Optional — defaults to `saudi_arabia` server-side. */
    country: registration.country,
    /** the carrier id that we should associate the vehicle with.
     *
     * by default, we will take the user org if it's a carrier company
     */
    carrierId: objectIdSchema.optional(),
    /** the driver id to assign the vehicle to */
    driverId: objectIdSchema.optional(),
    ownerType: vehicleOwnerTypeSchema.optional(),
    /** هوية المالك on the استمارة (70… company / bank). Never the driver 1…/2…. */
    ownerIdentity: idSchema
      .optional()
      .describe(
        'هوية المالك from the استمارة — 10 digits starting with 7 (company/bank). NEVER the driver 1…/2… iqama. Do not ask.',
      ),
    /**
     * What kind of party هوية المالك is: `company`, `individual`, or
     * `financier` (a bank / financing house holding the title — Bayan
     * tries its number last). Stored as `registration.ownerKind`.
     */
    ownerKind: vehicleOwnerKindSchema.optional(),
    /**
     * هوية المستخدم — who operates the truck: usually the driver 1…/2…,
     * or a 70… company when a company operates it. Stored as
     * `registration.operatorIdentity`. Not a Hero Org id. Not ownerIdentity.
     */
    operatorIdentity: idSchema
      .optional()
      .describe(
        'هوية المستخدم from the استمارة — the operator, almost always the driver 1…/2… id. Pass as operatorIdentity. NEVER put this in ownerIdentity.',
      ),
    /**
     * @deprecated Not stored as `carrierIdentity` (that is only what Bayan
     * accepted). A 70… sent here is saved as `registration.operatorIdentity`
     * when no operator is given, so the issuer still tries it right after
     * the proven numbers.
     */
    carrierIdentity: idSchema
      .optional()
      .describe(
        'Operating-company unified number (10 digits starting with 7). Pass from the extract, the Org, or what they typed. Do not invent it.',
      ),
    /** the name of the owner on the registration card ...
     * in case the carrier is not in our system, we add this.
     * */
    ownerLegalName: optionalStringSchema(nameSchema),
    /**
     * المنشأة / الشركة المستخدمة 70… number from the استمارة, when it is
     * clearly a company — not هوية المستخدم (the driver's 1…/2… id).
     * Stored as `registration.operatorIdentity` (a 70… operator wins over
     * a person's id).
     */
    vehicleUserEstablishmentId: idSchema
      .optional()
      .describe(
        'استمارة المنشأة المستخدمة (70… company number). Pass from the vision extract. Do not ask.',
      ),
    /** Body-type word printed on the card when it is not a Hero vehicle type (e.g. شاحنة). */
    vehicleTypeRaw: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe('Raw استمارة body-type word. Pass from the vision extract. Do not ask.'),
    /** Color word printed on the card when it is not a Hero color enum. */
    colorRaw: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe('Raw استمارة color word. Pass from the vision extract. Do not ask.'),
  });

//* create
export const vehicleCreateAPISchema = schema;
export type VehicleCreateAPI = API<z.infer<typeof vehicleCreateAPISchema>, Vehicle>;

//* update
export const vehicleUpdateAPISchema = schema.partial().extend({
  /** the vehicle id to update */
  _id: objectIdSchema,
});
export type VehicleUpdateAPI = API<z.infer<typeof vehicleUpdateAPISchema>, Vehicle>;

//* get
type QueryParam = {
  /** vehicle id as query param */
  id: string;
};
export type VehicleGetAPI = API<QueryParam, Vehicle>;

// * get many - vehicle list (carrier fleet)
export const vehicleCarrierListStatusFilterSchema = z
  .enum(['active', 'busy', 'no_driver'])
  .describe(
    "Fleet list status filter. 'active' / 'busy' map directly to `Vehicle.status`. 'no_driver' is a virtual filter — vehicles with no driver assigned, regardless of status.",
  );

/**
 * Filter-only schema for the carrier fleet query.
 *
 * Same role as {@link ordersQueryAPISchema} / {@link invoicesQueryAPISchema} /
 * {@link transactionsQueryAPISchema} — it carries the **filter** vocabulary
 * (no pagination, no search) so that:
 *   1. agent tool schemas can compose from it without inheriting
 *      request-level surface (`limit` / `skip`) the LLM shouldn't see;
 *   2. analytics / counting / grouping callers can build their own
 *      request shape around the same vocabulary;
 *   3. `vehicleGetManyAPISchema` (the public request schema) extends it
 *      with `paginationWithSearchSchema` for the existing API surface.
 *
 * Mapped to MongoDB by `VehicleQueryService.transformVehiclesQuery`.
 */
export const vehiclesQueryAPISchema = z.object({
  status: vehicleCarrierListStatusFilterSchema.optional(),

  /**
   * License plate. The backend normalizes both Latin and Arabic plate
   * formats (e.g. `"KSZ1234"`, `"مسك١٢٣٤"`, `"م س ك 1234"`) into a
   * single canonical form via `saudiVehiclePlateSchema`, so the caller
   * can pass plate text exactly as the user typed it.
   */
  plate: saudiVehiclePlateSchema
    .optional()
    .describe(
      'License plate. Backend normalizes Latin and Arabic plate formats into a single canonical form before exact match. Pass plate text as the user typed it.',
    ),

  /**
   * 3–4 digit plate-number fragment (e.g. `"9857"`, `"1231"`).
   * Matches vehicles whose canonical `registration.plate` **ends with**
   * these digits (Saudi plates are `LETTERS` + 4 digits). Exact
   * substring — not Atlas fuzzy — so `"9857"` does not match `"9858"`.
   * Ignored when `plate` is also set (full plate wins). Compose with
   * `driverName` to disambiguate ("9292 احمد").
   */
  plateDigits: z
    .string()
    .regex(/^\d{3,4}$/)
    .optional()
    .describe(
      '3–4 digit plate-number fragment. Exact end-anchored match on `registration.plate` (no fuzzy). Compose with `driverName` when several vehicles share the same digits.',
    ),

  /**
   * `Driver._id` (24-char Mongo ObjectId) — filter to vehicles
   * currently assigned to this driver. Resolve from
   * `search_drivers({ search })` when the user supplies a name.
   */
  driverId: objectIdSchema
    .optional()
    .describe(
      'Filter to vehicles assigned to a specific Driver (`Driver._id`, 24-char ObjectId). Resolve from `search_drivers` when the user references a driver by name.',
    ),

  /**
   * Substring match on the assigned driver's snapshot name
   * (`vehicle.driver.name`). Case-insensitive. Use when the caller
   * mentions a driver by partial name and a strict `driverId`
   * resolution is overkill.
   */
  driverName: nameSchema
    .optional()
    .describe(
      "Substring match on the assigned driver's snapshot name (`Vehicle.driver.name`). Case-insensitive. Prefer `driverId` when an exact match is needed.",
    ),

  /**
   * Filter by one or more `Vehicle.classification.type` values via
   * `$in`. Pass a single-element array for a single-type filter;
   * pass multiple to match any of them.
   */
  vehicleTypes: z
    .array(vehicleTypesSchema)
    .optional()
    .describe(
      'Filter to vehicles whose `classification.type` is in this array (`$in`). Pass one or more values. Same enum as `Vehicle.classification.type` (see `vehicleTypesSchema`).',
    ),
});
export type VehiclesQueryAPI = z.infer<typeof vehiclesQueryAPISchema>;

/**
 * Request schema for `GET /vehicle/carrier` — filters + pagination + search.
 *
 * Built by extending {@link vehiclesQueryAPISchema} with
 * {@link paginationWithSearchSchema}, mirroring how `myOrdersAPISchema`
 * and `InvoiceGetCurrentUserAPISchema` are constructed.
 */
export const vehicleGetManyAPISchema = paginationWithSearchSchema.extend({
  ...vehiclesQueryAPISchema.shape,
});
export type VehicleGetManyAPI = API<z.infer<typeof vehicleGetManyAPISchema>, Vehicle[]>;

/** this API is used to get the current user vehicle. Assuming the current has a `driver` type (freelancer or carrier driver) */
export type MyVehicleAPI = API<undefined, Vehicle | undefined>;

//* admin get all vehicles with optional filters
export const vehicleGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  /** Optional: filter by carrier/organization ID */
  orgId: objectIdSchema.optional(),
  /** Optional: filter by vehicle status */
  status: VehicleSchema.data.shape.status.optional(),
});
export type VehicleGetAllAdminAPI = API<z.infer<typeof vehicleGetAllAdminAPISchema>, Vehicle[]>;

/** Admin Create Vehicle API Schema */
export const vehicleCreateAdminAPISchema = vehicleCreateAPISchema
  .required({
    carrierId: true,
  })
  .extend({
    /** whether the carrier is the same as the owner. If not then you must supply the ownerLegalName */
    isCarrierSameAsOwner: z.boolean(),
  });
export type VehicleCreateAdminAPI = API<z.infer<typeof vehicleCreateAdminAPISchema>, Vehicle>;
